import { useCallback, useEffect, useRef, useState } from 'react'
import type { KeyboardEvent, ReactNode } from 'react'
import { createPortal } from 'react-dom'
import type { LeaveListItem } from '@lms/shared-types'
import { LEAVE_TYPE_ACCENT, LEAVE_TYPE_LABEL } from '../../lib/leave-type-meta'
import { fmtDateRangeLong, fmtSubmitted } from '../../lib/format'
import { displayName } from '../../lib/display-name'
import { Badge } from '../primitives/Badge'
import { Icon, type IconName } from '../primitives/Icon'
import { Tooltip } from '../primitives/Tooltip'
import {
  STATUS_BADGE_CLASS,
  STATUS_LABEL,
  computeDays,
} from '../employee/LeaveHistoryTable'

// Statuses a manager/approver can still act on from the queue.
const ACTIONABLE_STATUSES: ReadonlySet<LeaveListItem['status']> = new Set([
  'pending',
  'escalated',
  'pending_cancellation',
])

interface ApprovalsTableProps {
  leaves: ReadonlyArray<LeaveListItem>
  /** Open the shared leave detail page. */
  onOpen: (leaveId: string) => void
  /** Approve directly from the queue. Omit on read-only (history) tabs. */
  onApprove?: (leave: LeaveListItem) => void | Promise<void>
  /** Reject directly from the queue (opens the reject dialog). Omit on history. */
  onReject?: (leave: LeaveListItem) => void
  /** Overlapping pending requests per leave id — drives the Conflict badge. */
  conflictPeersByLeave?: ReadonlyMap<string, ReadonlyArray<LeaveListItem>>
  /** Rendered inside the card, flush beneath the table — e.g. a
   *  <PaginationControls> footer, so the bar reads as part of the table
   *  rather than a detached strip below it. */
  footer?: ReactNode
}

/**
 * Manager Approvals table — the same ``.card`` + ``.tbl`` vocabulary as the
 * employee LeaveHistoryTable, so Approvals and My Leave read identically. Adds
 * the approvals-only columns (Who / Submitted) and folds every card signal
 * (emergency, cancellation, conflict, start-countdown) into inline badges so no
 * data is lost. Row actions (Details / Approve / Reject) live in a ⋮ menu,
 * mirroring My Leave's row-actions menu.
 */
export function ApprovalsTable({
  leaves,
  onOpen,
  onApprove,
  onReject,
  conflictPeersByLeave,
  footer,
}: ApprovalsTableProps): JSX.Element {
  return (
    <div className="card" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
      <table className="tbl" style={{ minWidth: 760 }}>
        <thead>
          <tr>
            <th>Who</th>
            <th>Type</th>
            <th>Dates</th>
            <th>Days</th>
            <th>Submitted</th>
            <th>Status</th>
            <th aria-label="Flags" />
            <th aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {leaves.map((leave) => (
            <ApprovalRow
              key={leave.id}
              leave={leave}
              onOpen={onOpen}
              onApprove={onApprove}
              onReject={onReject}
              conflictPeers={conflictPeersByLeave?.get(leave.id) ?? []}
            />
          ))}
        </tbody>
      </table>
      {footer}
    </div>
  )
}

interface ApprovalRowProps {
  leave: LeaveListItem
  onOpen: (leaveId: string) => void
  onApprove: ((leave: LeaveListItem) => void | Promise<void>) | undefined
  onReject: ((leave: LeaveListItem) => void) | undefined
  conflictPeers: ReadonlyArray<LeaveListItem>
}

function ApprovalRow({
  leave,
  onOpen,
  onApprove,
  onReject,
  conflictPeers,
}: ApprovalRowProps): JSX.Element {
  const dateRange = fmtDateRangeLong(leave.startDate, leave.endDate)
  const days = computeDays(leave)
  const countdown = countdownLabel(leave)
  const canDecide = ACTIONABLE_STATUSES.has(leave.status)

  const handleOpen = (): void => onOpen(leave.id)
  const handleKeyDown = (event: KeyboardEvent<HTMLTableRowElement>): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleOpen()
    }
  }

  return (
    <tr
      data-testid="approval-row"
      data-leave-id={leave.id}
      data-status={leave.status}
      tabIndex={0}
      role="button"
      aria-label={`View ${displayName(leave.employee)}'s ${LEAVE_TYPE_LABEL[leave.type]} leave ${dateRange}`}
      onClick={handleOpen}
      onKeyDown={handleKeyDown}
      style={{ cursor: 'pointer' }}
    >
      <td style={{ fontWeight: 600, color: 'var(--text)', whiteSpace: 'nowrap' }}>
        {displayName(leave.employee)}
      </td>
      <td>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span
            aria-hidden
            style={{
              width: 8,
              height: 8,
              borderRadius: 4,
              background: LEAVE_TYPE_ACCENT[leave.type],
              flexShrink: 0,
            }}
          />
          {LEAVE_TYPE_LABEL[leave.type]}
          {leave.isEmergency ? (
            <Badge
              variant="danger"
              tooltip="Annual leave filed with less than 7 days notice. Classified as Emergency Leave (Policy §1.1)."
            >
              Emergency
            </Badge>
          ) : null}
          {leave.isOutOfSystem ? (
            <Badge
              variant="info"
              tooltip="Encoded by HR out of system (approved offline, e.g. unpaid or blackout-period leave)."
            >
              Out of system
            </Badge>
          ) : null}
          {leave.approvedOverCapacity ? (
            <Badge
              variant="warning"
              tooltip="Approved while the team was over its concurrent-absence cap. Recorded for HR oversight."
            >
              Above capacity
            </Badge>
          ) : null}
          {leave.status === 'pending_cancellation' ? (
            <Badge variant="warning">Cancellation requested</Badge>
          ) : null}
        </span>
      </td>
      <td style={{ fontSize: 12.5, whiteSpace: 'nowrap' }}>{dateRange}</td>
      <td style={{ fontVariantNumeric: 'tabular-nums' }}>{days}</td>
      <td style={{ color: 'var(--text-2)', fontSize: 12.5, whiteSpace: 'nowrap' }}>
        {fmtSubmitted(leave.createdAt)}
      </td>
      <td>
        <span className={STATUS_BADGE_CLASS[leave.status]}>
          {STATUS_LABEL[leave.status]}
        </span>
      </td>
      {/* Flags — conflict + timing as icons; the words live in the tooltip so
          the row stays uncluttered. Two fixed slots (conflict, then timing)
          keep the icons vertically aligned across rows even when one is
          absent. */}
      <td>
        <span
          style={{
            display: 'inline-grid',
            gridTemplateColumns: 'repeat(2, 18px)',
            gap: 8,
            justifyItems: 'center',
            alignItems: 'center',
          }}
        >
          <span>
            {conflictPeers.length > 0 ? (
              <FlagIcon
                name="warning"
                color="var(--danger-fg)"
                label={`Conflict — overlaps ${conflictPeers
                  .map((p) => `${displayName(p.employee)} (${fmtDateRangeLong(p.startDate, p.endDate)})`)
                  .join('; ')}`}
              />
            ) : null}
          </span>
          <span>
            {countdown ? (
              <FlagIcon
                name="schedule"
                color={countdown.overdue ? 'var(--warning)' : 'var(--text-3)'}
                label={countdown.label}
              />
            ) : null}
          </span>
        </span>
      </td>
      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        <RowActionsMenu
          leave={leave}
          onOpen={onOpen}
          {...(canDecide && onApprove ? { onApprove } : {})}
          {...(canDecide && onReject ? { onReject } : {})}
        />
      </td>
    </tr>
  )
}

/** "Starts in 3 days" / "Overdue" for a pending leave; null once decided.
 *  UTC + Date.now() so it's timezone-stable and honours a mocked clock. */
function countdownLabel(leave: LeaveListItem): { label: string; overdue: boolean } | null {
  if (!ACTIONABLE_STATUSES.has(leave.status)) return null
  const now = new Date(Date.now())
  const todayUtc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
  const start = new Date(`${leave.startDate}T00:00:00Z`).getTime()
  const diffDays = Math.round((start - todayUtc) / 86_400_000)
  if (diffDays < 0) return { label: 'Overdue', overdue: true }
  if (diffDays === 0) return { label: 'Starts today', overdue: true }
  if (diffDays === 1) return { label: 'Starts tomorrow', overdue: false }
  if (diffDays <= 7) return { label: `Starts in ${diffDays} days`, overdue: false }
  return null
}

interface FlagIconProps {
  name: IconName
  color: string
  /** Full text, shown as the native tooltip AND the accessible label so the
   *  icon is never a bare glyph to a screen reader. */
  label: string
}

/** A single row-flag: a colour-coded icon whose meaning lives in the tooltip.
 *  Keeps conflict/timing signals visible while cutting the pink-word clutter.
 *  Uses the app Tooltip with delay=0 so the meaning appears instantly on hover
 *  (the native `title` attribute has a browser-fixed ~1s delay). */
function FlagIcon({ name, color, label }: FlagIconProps): JSX.Element {
  return (
    <Tooltip content={label} delay={0}>
      <span
        role="img"
        aria-label={label}
        style={{ display: 'inline-flex', color, cursor: 'pointer' }}
      >
        <Icon name={name} size={18} aria-hidden />
      </span>
    </Tooltip>
  )
}

interface RowActionsMenuProps {
  leave: LeaveListItem
  onOpen: (leaveId: string) => void
  onApprove?: (leave: LeaveListItem) => void | Promise<void>
  onReject?: (leave: LeaveListItem) => void
}

/** Per-row ⋮ menu: Details / Approve / Reject. Mirrors LeaveHistoryTable's
 *  RowActionsMenu (Escape closes + restores focus, outside-click closes,
 *  ↑↓ move, flips above the trigger near the viewport bottom). */
function RowActionsMenu({
  leave,
  onOpen,
  onApprove,
  onReject,
}: Readonly<RowActionsMenuProps>): JSX.Element {
  const [open, setOpen] = useState(false)
  // Panel is portalled to <body> and positioned with `fixed` off the trigger's
  // viewport rect, so the surrounding `.card` overflow (overflowX: auto, which
  // also clips vertically) can never crop it — the bug this fixes was a short
  // card cropping the menu when only a few rows were present.
  const [coords, setCoords] = useState<{ right: number; top?: number; bottom?: number } | null>(
    null,
  )
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const itemCount = 1 + (onApprove ? 1 : 0) + (onReject ? 1 : 0)

  const reposition = useCallback((): void => {
    const rect = triggerRef.current?.getBoundingClientRect()
    if (!rect) return
    const right = window.innerWidth - rect.right
    const flipUp = rect.bottom + itemCount * 44 + 12 > window.innerHeight
    setCoords(
      flipUp
        ? { right, bottom: window.innerHeight - rect.top + 4 }
        : { right, top: rect.bottom + 4 },
    )
  }, [itemCount])

  useEffect(() => {
    if (!open) return
    function handleClick(e: MouseEvent): void {
      if (
        panelRef.current &&
        triggerRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  // Measure on open, then keep the panel glued to the trigger while the user
  // scrolls or resizes (capture:true catches scroll on the inner card too).
  useEffect(() => {
    if (!open) return
    reposition()
    window.addEventListener('scroll', reposition, true)
    window.addEventListener('resize', reposition)
    return () => {
      window.removeEventListener('scroll', reposition, true)
      window.removeEventListener('resize', reposition)
    }
  }, [open, reposition])

  useEffect(() => {
    if (!open) return
    const id = setTimeout(() => {
      panelRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus()
    }, 0)
    return () => clearTimeout(id)
  }, [open])

  const close = useCallback((): void => {
    setOpen(false)
    triggerRef.current?.focus()
  }, [])

  const handlePanelKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>): void => {
      if (e.key === 'Escape') {
        close()
        return
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault()
        const items = Array.from(
          panelRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [],
        )
        if (items.length === 0) return
        const idx = items.findIndex((el) => el === document.activeElement)
        const next =
          e.key === 'ArrowDown'
            ? (idx + 1) % items.length
            : (idx - 1 + items.length) % items.length
        items[next]?.focus()
      }
    },
    [close],
  )

  return (
    <div style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Row actions"
        data-testid="approval-row-actions"
        onClick={(event) => {
          event.stopPropagation()
          setOpen((prev) => !prev)
        }}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 32,
          height: 32,
          padding: 0,
          background: open ? 'var(--surface-2)' : 'transparent',
          border: '1px solid transparent',
          borderRadius: 8,
          cursor: 'pointer',
          color: 'var(--text-2)',
          transition: 'background var(--motion-duration-fast) var(--motion-ease-out)',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'var(--surface-2)'
        }}
        onMouseLeave={(e) => {
          if (!open) e.currentTarget.style.background = 'transparent'
        }}
      >
        <Icon name="more_vert" size={18} aria-hidden />
      </button>

      {open && coords &&
        createPortal(
        <div
          ref={panelRef}
          role="menu"
          data-testid="approval-row-actions-menu"
          onKeyDown={handlePanelKeyDown}
          onClick={(event) => event.stopPropagation()}
          style={{
            position: 'fixed',
            right: coords.right,
            ...(coords.top !== undefined ? { top: coords.top } : {}),
            ...(coords.bottom !== undefined ? { bottom: coords.bottom } : {}),
            minWidth: 176,
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 10,
            boxShadow: 'var(--shadow-2)',
            zIndex: 100,
            overflow: 'hidden',
            padding: '6px 0',
            animation: 'lms-row-actions-in var(--motion-duration-fast) var(--motion-ease-out)',
          }}
        >
          <style>{`
            @keyframes lms-row-actions-in {
              from { opacity: 0; transform: translateY(-4px); }
              to   { opacity: 1; transform: translateY(0); }
            }
            @media (prefers-reduced-motion: reduce) {
              @keyframes lms-row-actions-in {
                from { opacity: 0; }
                to   { opacity: 1; }
              }
            }
          `}</style>

          <RowActionItem
            icon="visibility"
            label="Details"
            testId="approval-details-button"
            onClick={() => {
              close()
              onOpen(leave.id)
            }}
          />
          {onApprove ? (
            <RowActionItem
              icon="check"
              label="Approve"
              testId="approval-approve-button"
              onClick={() => {
                close()
                void onApprove(leave)
              }}
            />
          ) : null}
          {onReject ? (
            <RowActionItem
              icon="close"
              label="Reject"
              testId="approval-reject-button"
              destructive
              onClick={() => {
                close()
                onReject(leave)
              }}
            />
          ) : null}
        </div>,
        document.body,
      )}
    </div>
  )
}

interface RowActionItemProps {
  icon: IconName
  label: string
  onClick: () => void
  testId: string
  destructive?: boolean
}

function RowActionItem({
  icon,
  label,
  onClick,
  testId,
  destructive = false,
}: Readonly<RowActionItemProps>): JSX.Element {
  const [hover, setHover] = useState(false)
  return (
    <button
      type="button"
      role="menuitem"
      data-testid={testId}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 14px',
        background: hover
          ? destructive
            ? 'color-mix(in srgb, var(--danger) 8%, transparent)'
            : 'var(--surface-2)'
          : 'transparent',
        border: 'none',
        cursor: 'pointer',
        color: destructive ? 'var(--danger)' : 'var(--text)',
        fontFamily: 'var(--font-sans)',
        fontSize: 13,
        textAlign: 'left',
        transition: 'background var(--motion-duration-fast) var(--motion-ease-out)',
        outline: 'none',
      }}
    >
      <Icon name={icon} size={16} aria-hidden />
      {label}
    </button>
  )
}
